'use client';

import { useEffect, useRef, useState } from 'react';
import { PanelShell } from './panel-shell';
import { closeWorkspacePanel, getWorkspacePanelState, useWorkspacePanelOpen } from './panel-events';
import { NotificationsView } from '@/components/notifications/notifications-view';
import { useDesktopFullscreenShownId, useOverviewMode } from '@/components/workspace/store';
import { useDiscoverOpen } from '@/components/workspace/discover/discover-events';

/** The card around the view: marked open on a desktop, the sheet's `dialog` below md. */
function cardAround(mark: HTMLElement | null): HTMLElement | null {
  return (mark?.closest('[data-ws-panel-open], [role="dialog"]') as HTMLElement | null) ?? null;
}

/**
 * The window's height while the card is open. The card is capped at 85% of
 * it, and the cap used to be read once, as the card opened: shrink the
 * window with the card open and the card kept its height and ran past the
 * bottom edge (510 px in a window dragged down to 400, the browser pass of
 * 2026-10-03). Only listened to while open; zero before the first read.
 */
function useWindowHeight(active: boolean): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (!active) return;
    const read = () => setHeight(window.innerHeight);
    read();
    window.addEventListener('resize', read);
    return () => window.removeEventListener('resize', read);
  }, [active]);
  return height;
}

/**
 * Top edge panel: Notifications.
 *
 * Renders the shared `NotificationsView` directly inside the panel — no
 * iframe, no app shell, no sidebars. The panel has no closed-state
 * handle (`noClosedHandle`): it opens only when asked, by the bell of the
 * legacy top chrome and, since 2026-10-03, by the Notifications row of a
 * full-screen app's ⋯ menu (components/workspace/nodes/node-frame.tsx),
 * where the rail and its account panel are not on the page.
 *
 * OVER A FULL-SCREEN APP, A PRESS ELSEWHERE CLOSES IT (2026-10-03). The
 * card has a close button and Esc, and under the legacy bell that was
 * enough: the bell toggled it. Opened from a menu over an app that fills
 * the page, it would stay over the app's corner until the person found the
 * X, the same defect the bar's own dropdown had (app-bar-menu.tsx, "A
 * PRESS INSIDE THE APP CLOSES THE DROPDOWN TOO"). So while a window is
 * full screen on this device, a press outside the card closes it. The app
 * is a frame and this page never sees a press inside it; the one signal it
 * gets is its window losing focus to the frame, so that closes it too.
 * NOT outside full screen: the legacy bell's own press would close the card
 * a moment before its click toggled it open again.
 *
 * "OVER A FULL-SCREEN APP" MEANS THE APP IS WHAT THE PAGE SHOWS (the visual
 * pass of 2026-10-03). The store's "a full-screen window is on view" follows
 * the current screen alone, and Stage does not change the current screen:
 * with the card open, the Stage chord drew Stage and left the card floating
 * over its tiles. Discover is the same shape (it covers the canvas without
 * moving the screen). So the card counts as over a full-screen app only
 * while neither is up, and the moment one opens it closes like any other
 * leaving of that view.
 *
 * THE KEYBOARD GOES WITH THE CARD (same pass). The menu hands the focus back
 * to ⋯ when its row is chosen, and the card sits after the app's frame in
 * the document: the next Tab went from ⋯ into the app, the window lost
 * focus to the frame, and the rule above closed the card. Opened from the
 * keyboard it could not be reached by the keyboard. So the card takes the
 * focus when it opens over a full-screen app (it is a region script may
 * focus, `cardFocusable`), Tab walks its own controls, and when it closes
 * with the keyboard still in it the focus returns to where it came from,
 * the bar's ⋯. A card closed because the person pressed inside the app
 * leaves the focus in the app: it is theirs (app-bar-menu.tsx, same rule).
 */
export function TopPanel() {
  const open = useWorkspacePanelOpen('top');
  const fullScreenShown = !!useDesktopFullscreenShownId();
  const stageUp = useOverviewMode();
  const discoverUp = useDiscoverOpen();
  const overFullScreen = fullScreenShown && !stageUp && !discoverUp;
  const windowHeight = useWindowHeight(open);
  const markRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open || !overFullScreen) return;
    let live = true;
    const onPress = (e: PointerEvent) => {
      const target = e.target as Node | null;
      const card = cardAround(markRef.current);
      if (!target || card?.contains(target)) return;
      closeWorkspacePanel('top');
    };
    // Read one tick later: some engines move the active element after the
    // blur event, not before.
    const onWindowBlur = () => {
      window.setTimeout(() => {
        if (!live) return;
        if (document.activeElement instanceof HTMLIFrameElement) closeWorkspacePanel('top');
      }, 0);
    };
    document.addEventListener('pointerdown', onPress, true);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      live = false;
      document.removeEventListener('pointerdown', onPress, true);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [open, overFullScreen]);

  // The keyboard goes with the card (see the file note). Declared BEFORE
  // the effect below on purpose: when Stage or Discover opens, this
  // cleanup runs while the card is still in the document and can still
  // tell whether the keyboard was in it.
  useEffect(() => {
    if (!open || !overFullScreen) return;
    const card = cardAround(markRef.current);
    if (!card) return;
    const opener = document.activeElement instanceof HTMLElement && document.activeElement !== document.body
      ? document.activeElement
      : null;
    card.focus({ preventScroll: true });
    // The shell could not take it (the sheet below md is not focusable by
    // script): the keyboard never moved, so there is nothing to give back.
    if (document.activeElement !== card) return;
    return () => {
      const active = document.activeElement;
      // Still in the card, or nowhere because the card left the document
      // with the focus inside it. Anywhere else (the app's frame, a field
      // of Discover) the keyboard has already been taken somewhere on
      // purpose.
      const withCard = !active || active === document.body || card.contains(active);
      if (withCard && opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open, overFullScreen]);

  // The card belongs to the full-screen view it was opened over: leaving
  // that view by a way that is not a press (Esc with the card closed is
  // one press later, but the screen chords, Stage and Discover are not
  // presses on this page) closes it, so it is never left floating over
  // the ordinary workspace, where the rail's own Notifications is, or
  // over Stage's tiles.
  const wasOverFullScreen = useRef(overFullScreen);
  useEffect(() => {
    if (wasOverFullScreen.current && !overFullScreen && getWorkspacePanelState('top')) {
      closeWorkspacePanel('top');
    }
    wasOverFullScreen.current = overFullScreen;
  }, [overFullScreen]);

  // Notifications opens as a popover-style card anchored to the top-
  // right gutter on desktop — i.e. it reads as a dropdown under the
  // bell FAB (or under a full-screen bar's ⋯), not as a full-width edge
  // panel. On mobile it falls back to a vertical sheet (handled inside
  // PanelShell).
  return (
    <PanelShell
      edge="top"
      id="top"
      ariaLabel="Notifications"
      defaultSize={560}
      minSize={320}
      maxSize={
        // The live height while open; the height at this render before the
        // first read (the card's opening frame); a fixed number on the server.
        windowHeight > 0
          ? Math.round(windowHeight * 0.85)
          : typeof window !== 'undefined'
            ? Math.round(window.innerHeight * 0.85)
            : 720
      }
      desktopPlacement="top-right"
      desktopWidth={420}
      noClosedHandle
      cardFocusable
    >
      {/*
        The shared NotificationsView renders its own title + bulk action
        cluster (Mark all read / Clear read / Dismiss all). Letting it
        render the header keeps the workspace panel in sync with the
        full-page version automatically. We add right padding via a
        sibling spacer in the action area so the panel's top-right X
        close button doesn't overlap the bulk-action buttons.
      */}
      {/* A marker inside the card, so the press handler above can find the
          card without the shell handing out its own element. Hidden: it
          takes no part in the layout. */}
      <span ref={markRef} hidden />
      <NotificationsView
        showTitle
        className="[&>div:first-child]:pr-12"
      />
    </PanelShell>
  );
}
